import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import SearchField from "./SearchField";
import { TableSizeSelect } from "./TablePaginator";
import { useTableSearch } from "../../util/tableSearch";

/**
 * 표 우측 상단 공통 컨트롤 — [검색] [N개씩 보기].
 *
 * <h3>왜 한 컴포넌트로 묶었나</h3>
 * <p>둘 다 "표를 보기 전에 범위를 좁히는" 조작이라 붙어 있어야 손이 덜 간다. 예전엔 화면마다
 * 검색창 위치·모양·디바운스 시간이 제각각이었고(어떤 곳은 즉시, 어떤 곳은 300ms, 어떤 곳은 엔터),
 * 페이지 크기 선택은 아예 없는 표도 있었다. 여기로 모으면 표가 몇 개든 조작법이 같다.
 *
 * <h3>검색 — 표 안에서 훑는 용도, 컬럼 지정 없이</h3>
 * <p>이 검색은 "지금 이 표에 보이는 것 중에서 찾기" 다. 그래서 어느 컬럼을 볼지 미리 정하지 않고
 * {@link useTableSearch} 가 <b>행의 모든 값</b>을 훑는다 — 표마다 검색 대상 필드를 나열하고
 * 관리하는 일이 없어진다. 특정 컬럼만 보고 싶은 표만 예외로 필드를 넘기면 된다.
 *
 * <p>서버에 질의해야 하는 표(전체를 안 들고 있는 경우)는 {@code onSearch} 로 검색어만 받아
 * 직접 조회하면 된다 — 이 컴포넌트는 디바운스된 문자열을 올려줄 뿐 필터링에 관여하지 않는다.
 *
 * <h3>N개씩 보기는 '지원하는 표에만'</h3>
 * <p>{@code onPageSizeChange} 를 넘긴 표에만 나타난다. 페이지 크기를 바꿔도 반영할 수 없는 표에
 * 컨트롤만 띄우면 눌러도 아무 일이 없어 오히려 혼란스럽다.
 *
 * @param {string}   [search]        현재 검색어(제어 컴포넌트로 쓸 때)
 * @param {function} [onSearch]      디바운스된 검색어 콜백. 없으면 검색창을 그리지 않는다.
 * @param {string}   [placeholder]
 * @param {number}   [debounceMs=300]
 * @param {number}   [pageSize]      N개씩 보기 현재값
 * @param {function} [onPageSizeChange] 없으면 개수 선택을 그리지 않는다.
 * @param {number[]} [pageSizeOptions]
 * @param {ReactNode}[left]          좌측에 함께 놓을 것(필터 칩 등). 없으면 컨트롤이 우측으로 붙는다.
 * @param {ReactNode}[actions]       등록·관리 같은 동작 버튼. <b>가장 우측</b>에 놓인다(검색은 그 왼쪽).
 * @param {object}   [sx]            루트 sx 덮어쓰기 — 기존 필터 줄 안에 끼울 때 여백/신축을 맞춘다.
 */
export default function TableToolbar({
    search,
    onSearch,
    placeholder = "검색",
    debounceMs = 300,
    pageSize,
    onPageSizeChange,
    pageSizeOptions,
    left = null,
    actions = null,
    sx,
}) {
    const [text, setText] = useState(search ?? "");

    // 바깥에서 검색어를 바꾼 경우(초기화 버튼 등) 입력칸도 따라간다.
    useEffect(() => { if (search !== undefined) setText(search); }, [search]);

    // 타자마다 조회하면 서버·렌더가 요동친다. 입력이 멎은 뒤에만 올려보낸다.
    useEffect(() => {
        if (!onSearch) return;
        const t = setTimeout(() => onSearch(text.trim()), debounceMs);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, debounceMs]);

    return (
        <Box sx={{
            display: "flex", alignItems: "center", gap: 1,
            flexWrap: "wrap", mb: 1,
            // 이미 다른 필터 줄 안에 끼워 넣는 경우 여백·너비를 호출처가 조정할 수 있게 열어둔다.
            ...sx,
        }}>
            {left}
            {/* 컨트롤은 항상 우측 끝 — 표 폭이 달라져도 자리가 흔들리지 않는다. */}
            <Box sx={{ flex: 1, minWidth: 8 }} />

            {onSearch && (
                /* 시스템 공통 표 검색 디자인(SearchField)으로 통일 — 돋보기·지우기(✕)·연회색 배경.
                   상태·디바운스(onSearch) 배선은 그대로. 너비 260은 기존 툴바 레이아웃 유지를 위해
                   명시(SearchField 기본값 240 대신). onChange 는 값을 그대로 받으므로 setText 직결. */
                <SearchField
                    value={text}
                    onChange={setText}
                    placeholder={placeholder}
                    width={260}
                    sx={{ flexShrink: 0 }}
                />
            )}

            {onPageSizeChange && (
                <TableSizeSelect
                    value={pageSize}
                    onChange={onPageSizeChange}
                    options={pageSizeOptions}
                />
            )}

            {/* 동작 버튼(등록·관리 등)은 항상 맨 오른쪽. 조회 조건(검색·개수)을 먼저 만지고
                마지막에 행동하는 순서대로 왼→오 배치한다. */}
            {actions}
        </Box>
    );
}

// 검색 필터 구현은 util/tableSearch.js 한 곳에만 둔다. 여기서는 함께 쓰기 편하도록 다시 내보낸다.
export { useTableSearch };
